import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import * as React from 'react';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useIntl } from 'react-intl';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';
import useSearch from 'src/registry/hooks/useSearch';
import { useListTaxReasons } from 'src/features/taxReasons/api/useListTaxReasons';
import { createColumnHelper } from '@tanstack/react-table';
import { TaxReason } from 'src/features/taxReasons/utils/types';
import { commonMessages } from 'src/utils/messages';
import CreateTaxReasonFormModal from 'src/features/taxReasons/components/forms/CreateTaxReasonFormModal';
import CellTaxReasonActions from 'src/features/taxReasons/components/tables/CellTaxReasonActions';
import useFilters from 'src/registry/hooks/useFilters';
import { booleanBadgeOptions } from 'src/utils/constants';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const TaxReasonsTable = () => {
    const { formatMessage } = useIntl();

    const { setTitle, removeTitle } = useTitleStore();

    const permissionManageTaxReasons = useHasPermissions([PERMISSION.MANAGE_TAX_REASONS]);

    React.useEffect(() => {
        setTitle(formatMessage(taxReasonsMessages.taxReasons));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { querySearchOptions, ...search } = useSearch();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: taxReasons,
        isLoading,
        isError,
    } = useListTaxReasons({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<TaxReason>();

    const columns = [
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('value', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (row.original.value ? `${(Number(row.original.value) * 100).toFixed(2)}%` : '-'),
            meta: {
                title: formatMessage(taxReasonsMessages.value),
            },
        }),
        columnHelper.accessor('is_default', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(taxReasonsMessages.isDefault),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(taxReasonsMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageTaxReasons
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellTaxReasonActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<TaxReason, unknown>
            columns={columns}
            data={taxReasons}
            semantics={{
                title: formatMessage(taxReasonsMessages.taxReasons),
                entryName: formatMessage(taxReasonsMessages.reason).toLowerCase(),
                entryNames: formatMessage(taxReasonsMessages.reasons).toLowerCase(),
                placeholder: formatMessage(taxReasonsMessages.reason).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageTaxReasons && { newEntry: CreateTaxReasonFormModal }),
            }}
        />
    );
};

export default TaxReasonsTable;
